import { useEffect } from 'react';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { useShallow } from 'zustand/react/shallow';
import { useMutation } from '@apollo/client';
import useNotificationStore from '@/stores/useNotificationStore';
import SentryService from '@/services/sentry/Sentry.service';
import { IS_WEB } from '@/shared/constants/utility';
import {
  MutationResult,
  MutationSavePushTokenArgs,
} from '@/shared/types/generated';
import { SAVE_PUSH_TOKEN } from '@/shared/mutations';
import { useGetUser } from '@/shared/hooks/useGetUser';

export function useSavePushToken() {
  const { user } = useGetUser();

  const { pushToken, hasSavedPushToken, setHasSavedPushToken } =
    useNotificationStore(
      useShallow((state) => ({
        pushToken: state.pushToken,
        hasSavedPushToken: state.hasSavedPushToken,
        setHasSavedPushToken: state.setHasSavedPushToken,
      })),
    );

  const [savePushToken] = useMutation<
    MutationResult<'savePushToken'>,
    MutationSavePushTokenArgs
  >(SAVE_PUSH_TOKEN);

  useEffect(() => {
    const saveToken = async () => {
      if (IS_WEB || hasSavedPushToken) {
        return;
      }

      if (!user?.pushNotificationsEnabled) {
        __DEV__ && console.log('Push notifications not enabled for user');
        return;
      }

      if (!pushToken) {
        __DEV__ && console.log('No push token available to save');
        return;
      }

      try {
        const platform = Platform.OS;
        const deviceName =
          Device.deviceName || Device.modelName || 'Unknown Device';

        await savePushToken({
          variables: {
            input: {
              token: pushToken,
              platform,
              deviceName,
            },
          },
        });

        setHasSavedPushToken(true);
      } catch (error) {
        SentryService.captureError('Error saving push token to server', error);
      }
    };

    saveToken();
  }, [
    pushToken,
    hasSavedPushToken,
    savePushToken,
    setHasSavedPushToken,
    user?.pushNotificationsEnabled,
  ]);
}
